import { useState } from 'react';
import { ApiError } from '@/lib/api';
import { providerAppsApi, type ConnectionView } from '@/lib/connections';
import { Button, Card, Eyebrow, Field, Notice, StatusChip, TextButton } from '@/components/ui';

/**
 * The organisation's ZenSpace API key.
 *
 * WHY THIS CARD EXISTS AND WHY IT IS ON THIS SCREEN. Mapping a pod registers
 * two subscriptions: one on the customer's calendar, one on ZenCore. The second
 * needs this key, and without it every mapping stops at `partial` — the
 * calendar side live, ZenCore's side never — so a booking made in ZenSpace
 * never reaches the room. That failure is invisible until someone stands
 * outside a pod, and it is discovered AFTER mapping rather than before. So the
 * key is asked for here, beside the other credentials, and the checklist says
 * it is missing before anyone maps anything.
 *
 * Shown whether or not a calendar is connected. The key belongs to the
 * ORGANIZATION and lives in its own table, so supplying it first and connecting
 * second is a perfectly reasonable order — it used to be impossible, because
 * the key was a column on the connection row.
 */
export function ZenCoreKeyCard({
  connections,
  onSaved,
}: {
  connections: ConnectionView[];
  onSaved: () => void | Promise<void>;
}) {
  const configured = connections.some((c) => c.zencore_key_configured);

  const [editing, setEditing] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const result = await providerAppsApi.saveZenCoreKey(apiKey);
      setApiKey('');
      setEditing(false);
      setSaved(
        `Checked against ZenSpace — we can see ${result.pods_visible} ` +
          `space${result.pods_visible === 1 ? '' : 's'} with it.`,
      );
      await onSaved();
    } catch (err) {
      // The server's message names the likely cause, including the scope
      // spelling trap. It is written for this screen, so it is shown as-is.
      setError(err instanceof ApiError ? err.message : 'We could not save that key.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-[14px]">
        <div className="min-w-0">
          <Eyebrow>ZenSpace</Eyebrow>
          <h2 className="mt-[4px] mb-[8px] text-heading font-semibold text-ink">
            Your ZenSpace API key
          </h2>
          <StatusChip tone={configured ? 'healthy' : 'attention'}>
            {configured ? 'Stored and working' : 'Needed before you map any spaces'}
          </StatusChip>
        </div>
      </div>

      <p className="mt-[14px] mb-0 max-w-[70ch] text-body text-muted">
        {configured
          ? 'We use this to subscribe to your booking events and to write bookings back. Replace it if you rotate the key.'
          : 'Mapping a space subscribes to its bookings on both sides. Without this key only the calendar side registers — a booking made in ZenSpace would never reach the room.'}
      </p>

      {saved && !editing && (
        <div className="mt-[14px]">
          <Notice tone="healthy" title={saved} />
        </div>
      )}

      {configured && !editing ? (
        <div className="mt-[16px]">
          <TextButton type="button" onClick={() => setEditing(true)}>
            Replace key
          </TextButton>
        </div>
      ) : (
        <form className="mt-[16px] flex flex-col gap-[14px]" onSubmit={(e) => void save(e)}>
          <Field
            label="ZenSpace API key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            hint="Created in ZenSpace for THIS organization. We check it before saving, and never show it again."
            autoComplete="new-password"
            spellCheck={false}
            required
          />

          {error && <Notice tone="broken" title={error} />}

          <div className="flex flex-wrap items-center gap-[12px]">
            <Button type="submit" tone="primary" busy={saving} disabled={!apiKey.trim()}>
              Check and save
            </Button>
            {configured && (
              <TextButton
                type="button"
                onClick={() => {
                  setEditing(false);
                  setError(null);
                }}
              >
                Cancel
              </TextButton>
            )}
          </div>
        </form>
      )}

      <details className="mt-[16px]">
        <summary className="cursor-pointer text-body text-brand">
          What the key has to be allowed to do
        </summary>
        <ul className="mt-[8px] mb-0 flex list-none flex-col gap-[6px] p-0">
          {[
            'create:webhooks',
            'read:webhooks',
            'delete:webhooks',
            'read:bookings',
            'create:bookings',
            'update:bookings',
            'read:meetingSpaces',
          ].map((scope) => (
            <li key={scope} className="font-mono text-sub text-muted">
              {scope}
            </li>
          ))}
        </ul>
        <p className="mt-[10px] mb-0 max-w-[70ch] text-caption text-muted">
          The spelling matters — <code>read:meeting-spaces</code> does not satisfy{' '}
          <code>read:meetingSpaces</code>. The key also needs a valid creating user: if it is
          accepted here but every mapping still fails to register, that is why.
        </p>
      </details>
    </Card>
  );
}
